import {
  groupResizeFromEnd,
  groupResizeFromStart,
  resizeAgainstNeighbor,
} from '@diagram/utils/geometry';
import { ResizePatch } from '../store.types';
import {
  NodeId,
  Side,
  SwimlaneGroupNodeModel,
  SwimlaneNodeModel,
} from '@diagram/model/node.model';
import { ChangeModel } from '@diagram/model/change.model';
import { Orientation } from '@diagram/model/node.model';

export type Axis = 'x' | 'y';
export type Edge = 'start' | 'end';

type AxisKind = 'primary' | 'cross';

export interface Lane {
  id: string;
  size: number; // width if axis='x', height if axis='y'
  min: number; // per-lane min size
  pos: number; // lane start along primary axis (computed)
}

export interface Group {
  start: number; // group start along axis (x or y)
  gutter: number; // gap between lanes
  lanes: Lane[]; // in visual order front→back
  groupLane: Lane; // the swimlane representing the group itself
}

interface CanonOp {
  axis: Axis;
  relAxis: AxisKind; // 'primary' or 'cross'
  edge?: Edge; // present iff axis === 'primary'
  d: number; // signed delta along the chosen axis (+ toward 'end')
  shift: boolean; // shiftKey
  activeIndex: number;
}

const buildGroupView = (
  groupNode: SwimlaneGroupNodeModel,
  laneNodes: SwimlaneNodeModel[],
  gutter: number = 0,
  side: Side
): Group => {
  const minWidth = 150;
  const minHeight = 150;
  const vertical = groupNode.data.orientation === 'vertical';
  const start = vertical ? groupNode.position.x : groupNode.position.y;
  const lanes: Lane[] = laneNodes.map((n) => {
    return side === 'left' || side === 'right'
      ? { id: n.id, size: n.width, min: minWidth, pos: n.position.x }
      : { id: n.id, size: n.height, min: minHeight, pos: n.position.y };
  });
  return {
    start,
    gutter,
    lanes,
    groupLane: {
      id: groupNode.id as NodeId,
      ...(side === 'left' || side === 'right'
        ? { size: groupNode.width, min: minWidth, pos: groupNode.position.x }
        : {
            size: groupNode.height,
            min: minHeight,
            pos: groupNode.position.y,
          }),
    },
  };
};

const toCanon = (
  orientation: Orientation,
  side: Side,
  dx: number,
  dy: number,
  shiftKey: boolean,
  activeIndex: number
): CanonOp => {
  const vertical = orientation === 'vertical';
  const axis = dx !== 0 ? 'x' : 'y';

  // Primary edges depend on orientation
  const isPrimaryEdge =
    (vertical && (side === 'left' || side === 'right')) ||
    (!vertical && (side === 'top' || side === 'bottom'));

  if (isPrimaryEdge) {
    // Map side → lane-local start/end edge
    const edge: Edge = vertical
      ? side === 'left'
        ? 'start'
        : 'end'
      : side === 'top'
      ? 'start'
      : 'end';

    // Project motion onto the primary axis; + means toward 'end' (right or down)
    const d = vertical ? dx : dy;

    return { axis, relAxis: 'primary', edge, d, shift: shiftKey, activeIndex };
  } else {
    // Cross-axis: top/bottom for vertical, left/right for horizontal
    // Project onto cross axis; + means increasing cross size (down for vertical, right for horizontal)
    const d = vertical ? dy : dx;
    return { axis, relAxis: 'cross', d, shift: shiftKey, activeIndex };
  }
};

const applyResizeEntry = (
  group: Group, // same Group/Lane structs from earlier
  orientation: Orientation,
  side: Side,
  dx: number,
  dy: number,
  shiftKey: boolean,
  activeIndex: number
): Group => {
  const op = toCanon(orientation, side, dx, dy, shiftKey, activeIndex);

  // handling height changes on vertical swimlanes / width changes on horizontal swimlanes
  if (op.relAxis === 'cross') {
    // cross-axis resizing, no position adjustment
    if (side === 'right' || side === 'bottom') {
      const size = group.groupLane.size + op.d;
      const lanes = group.lanes.map((l) => ({
        ...l,
        size: size,
      }));

      const groupLane = {
        ...group.groupLane,
        size: size,
      };

      return { ...group, lanes: [...lanes, groupLane] };
    } else if (side === 'left' || side === 'top') {
      const size = group.groupLane.size - op.d;
      const pos = group.groupLane.pos + op.d;
      const lanes = group.lanes.map((l) => {
        return { ...l, size: size, pos: 0 };
      });
      const groupLane = {
        ...group.groupLane,
        size: size,
        pos,
      };
      return { ...group, lanes: [...lanes, groupLane] };
    }
  }

  // Primary-axis path (neighbor push/pull, group-edge behavior)
  const edge = op.edge!; // defined in primary branch
  const i = op.activeIndex;
  const isFront = i === 0;
  const isBack = i === group.lanes.length - 1;

  // if (op.shift) {
  //   return edge === 'start'
  //     ? groupResizeFromStart(group, op.d)
  //     : groupResizeFromEnd(group, op.d);
  // }

  if (edge === 'start' && isFront) return groupResizeFromStart(group, op.d);
  if (edge === 'end' && isBack) return groupResizeFromEnd(group, op.d);

  // Otherwise push–pull with the nearest neighbor on the dragged edge
  if (edge === 'start') {
    // resizing active lane at its start edge ⇒ neighbor is in 'front'
    return resizeAgainstNeighbor(group, activeIndex, 'front', op.d);
  } else {
    // edge === 'end' ⇒ neighbor is in 'back'
    const test = resizeAgainstNeighbor(group, activeIndex, 'back', op.d);
    return test;
  }
};

export const applyResizeAndMakePatches = (args: {
  orientation: Orientation;
  dx: number;
  dy: number;
  side: Side;
  shiftKey: boolean;
  groupNode: SwimlaneGroupNodeModel;
  laneNodes: SwimlaneNodeModel[];
  activeLaneId: string;
  gutter?: number;
}): { patches: ResizePatch[]; changes: ChangeModel[] } => {
  const {
    orientation,
    dx,
    dy,
    side,
    shiftKey,
    groupNode,
    laneNodes,
    activeLaneId,
    gutter = 0,
  } = args;

  const group = buildGroupView(groupNode, laneNodes, gutter, side);
  const activeIndex = group.lanes.findIndex((l) => l.id === activeLaneId);

  const updated = applyResizeEntry(
    group,
    orientation,
    side,
    dx,
    dy,
    shiftKey,
    activeIndex
  );

  // Build node patches
  const patches: ResizePatch[] = [];
  const changes: ChangeModel[] = [];
  // const vertical = orientation === 'vertical';

  for (const l of updated.lanes) {
    const id = l.id as NodeId;
    const patch =
      side === 'left' || side === 'right'
        ? { x: l.pos, width: l.size }
        : { y: l.pos, height: l.size };
    patches.push({ id, ...patch });
    // changes.push(
    //   createChange('resize', {
    //     id: id as NodeId,
    //     status: 'dragging' as Status,
    //     x: vertical ? l.pos : laneNodesById[id].position.x,
    //     y: vertical ? laneNodesById[id].position.y : l.pos,
    //     width: vertical ? l.size : laneNodesById[id].width,
    //     height: vertical ? laneNodesById[id].height : l.size,
    //   })
    // );
  }

  // const totalPrimary = updated.lanes.reduce(
  //   (acc, lane, i) => acc + lane.size + (i > 0 ? updated.gutter : 0),
  //   0
  // );

  // const groupPatch =
  //   side === 'left' || side === 'right'
  //     ? {
  //         id: groupNode.id as NodeId,
  //         x: groupNode.position.x,
  //         width: updated.lanes[0].size,
  //       }
  //     : {
  //         id: groupNode.id as NodeId,
  //         y: groupNode.position.y,
  //         height: totalPrimary,
  //       };

  // patches.unshift(groupPatch);
  // changes.unshift(
  //   createChange('resize', { status: 'dragging' as Status, ...groupPatch })
  // );
  return { patches, changes };
};
